import { useEffect, useRef, useState } from 'react';
import {
  calendarGrid,
  formatMonthYear,
  formatWeekdayShort,
  formatDayMonth,
  sameDay,
  nextWeekISO,
  todayISO,
  tomorrowISO,
} from '../lib/dates';
import './DatePicker.css';

interface Props {
  value: string | null;
  onPick: (iso: string | null) => void;
  onClose: () => void;
}

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** Compact calendar plus the three options that cover most of the real cases. */
export function DatePicker({ value, onPick, onClose }: Props) {
  const selected = value ? new Date(value) : null;
  const [month, setMonth] = useState(() => selected ?? new Date());
  const root = useRef<HTMLDivElement>(null);
  const today = new Date();

  useEffect(() => {
    root.current?.focus();
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  const shift = (by: number) =>
    setMonth((m) => new Date(m.getFullYear(), m.getMonth() + by, 1));

  const quick = [
    { label: 'Today', hint: formatWeekdayShort(todayISO()), iso: todayISO() },
    { label: 'Tomorrow', hint: formatWeekdayShort(tomorrowISO()), iso: tomorrowISO() },
    { label: 'Next week', hint: formatDayMonth(nextWeekISO()), iso: nextWeekISO() },
  ];

  return (
    <div
      className="dp"
      ref={root}
      tabIndex={-1}
      role="dialog"
      aria-label="Pick a date"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="dp__quick">
        {quick.map((q) => (
          <button
            key={q.label}
            type="button"
            className={`dp__option${selected && sameDay(selected, new Date(q.iso)) ? ' is-selected' : ''}`}
            onClick={() => onPick(q.iso)}
          >
            <span>{q.label}</span>
            <span className="dp__hint">{q.hint}</span>
          </button>
        ))}
        <button type="button" className="dp__option dp__option--none" onClick={() => onPick(null)}>
          No date
        </button>
      </div>

      <div className="dp__rule" />

      <div className="dp__month">
        <button type="button" className="dp__nav" aria-label="Previous month" onClick={() => shift(-1)}>
          ‹
        </button>
        <span className="dp__monthName">{formatMonthYear(month)}</span>
        <button type="button" className="dp__nav" aria-label="Next month" onClick={() => shift(1)}>
          ›
        </button>
      </div>

      <div className="dp__grid">
        {DOW.map((d, i) => (
          <span className="dp__dow" key={i}>
            {d}
          </span>
        ))}
        {calendarGrid(month).map((day) => (
          <button
            key={day.toISOString()}
            type="button"
            className={[
              'dp__day',
              day.getMonth() !== month.getMonth() && 'is-outside',
              selected && sameDay(day, selected) && 'is-selected',
              sameDay(day, today) && 'is-today',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              const picked = new Date(day);
              // Keep the clock time that was already set; only the day changes.
              if (selected) picked.setHours(selected.getHours(), selected.getMinutes(), 0, 0);
              onPick(picked.toISOString());
            }}
          >
            {day.getDate()}
          </button>
        ))}
      </div>

      <div className="dp__foot">
        <span>{selected ? formatDayMonth(selected.toISOString()) : 'No date set'}</span>
        <span className="kbd">esc</span>
      </div>
    </div>
  );
}
